import { createSubscribableSelector } from './createSubscribableSelector';

class AbortError extends Error {
    public override readonly name = 'AbortError';
}

type Options = {
    signal?: AbortSignal;
};

type Result = Promise<void> & {
    /**
     * Функция для ручного вызова селектора. Может быть полезной для кастомных оберток
     * в сервисах с собственным глобальным стейтом.
     *
     * Если вызов селектора вернет `true`, то промис будет зарезолвлен.
     */
    checkSelectorValueManual(): void;
};

/**
 * Дожидается когда селектор вернет `true`.
 *
 * @param selector Селектор, за изменениями значения которого нужно следить
 * @param options Параметры. Могут содержать `AbortSignal`, отменяющий подписку на селектор
 * @returns Promise, который зарезолвится когда селектор вернет `true`
 */
export function waitForSelector(selector: () => boolean, { signal }: Options = {}): Result {
    let checkSelectorValue: () => void;
    const { select, subscribeToSelector, subscribeToStores } = createSubscribableSelector(selector);
    const unsubscribeStores = subscribeToStores();

    const promise = new Promise<void>((resolve, reject) => {
        function handleAbort() {
            unsubscribeStores();
            unsubscribeCallback();
            reject(new AbortError('Aborted'));
        }

        checkSelectorValue = () => {
            if (select()) {
                unsubscribeStores();
                unsubscribeCallback();
                signal?.removeEventListener('abort', handleAbort);
                resolve();
            }
        };

        const unsubscribeCallback = subscribeToSelector(checkSelectorValue);

        if (signal?.aborted) {
            handleAbort();

            return;
        }

        signal?.addEventListener('abort', handleAbort, { once: true });

        checkSelectorValue();
    });

    Object.assign(promise, {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- DIRECT-239681
        checkSelectorValueManual: checkSelectorValue!,
    });

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    return promise as Result;
}
